import type { ExpenseBreakdown, MoneyIn, PlanningNatureSlice } from '@pfo/analytics';
import { currentPeriod, type Period } from '@pfo/shared';
import { useId, useState } from 'react';

import { cx } from '../../../shared/lib/cx.js';
import { formatVnd } from '../../../shared/lib/money.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { AllocationCard } from '../../home/components/AllocationCard.js';
import { MonthSwitcher } from '../../home/components/MonthSwitcher.js';
import { useHouseholdMoneyFlow, useMoneyFlow } from '../../home/hooks/useMoneyFlow.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { useUnexpected } from '../hooks/useReport.js';
import {
  isStable,
  monthLabel,
  PLANNING_NATURE_LABELS,
  STABILITY_LABELS,
} from '../model/reportLabels.js';
import type { UnexpectedSummary } from '../model/unexpectedSummary.js';
import styles from './ReportPage.module.css';

/**
 * "Báo cáo" — the detail behind the dashboard (plan step 1.4, the part left after the Home
 * screen).
 *
 * Three questions the Trang chủ deliberately does not answer:
 *   1. Where did the money come IN from, and how much of it can be planned around (B10)?
 *   2. What was the month's spending BY NATURE rather than by category (§10)?
 *   3. How much does this household actually need to hold back for surprises (§10)?
 *
 * The third one looks back a year, not at the month on screen: one bad month says nothing.
 *
 * Step 1.8 adds the `Me | Gia đình` switch of spec 22.3. It appears only once a family
 * exists, and it always opens on Me: this product is individual-first (A1), so the household
 * view is something a person asks for, never the default they are given.
 */
export function ReportPage() {
  const contextState = useCurrentContext();
  const natureHeadingId = useId();
  const [scope, setScope] = useState<ReportScope>('me');

  const timeZone =
    contextState.status === 'ready' ? contextState.context.household.timezone : 'UTC';
  const current = currentPeriod(timeZone);
  const [period, setPeriod] = useState<Period | null>(null);
  const shown = period ?? current;

  const subjectId = contextState.status === 'ready' ? contextState.context.selfSubject.id : '';
  const isFamily = contextState.status === 'ready' && contextState.context.isFamily;
  const householdId = contextState.status === 'ready' ? contextState.context.household.id : '';
  // The switch can only be on `family` while a family exists, so a household that was never
  // created cannot be read by flipping a piece of component state.
  const showFamily = isFamily && scope === 'family';

  // Whichever view is not on screen is passed null, so the two answers can never be mixed.
  const personalFlow = useMoneyFlow(
    subjectId === '' || showFamily ? null : { subjectId, year: shown.year, month: shown.month },
  );
  const familyFlow = useHouseholdMoneyFlow(
    showFamily ? { householdId, year: shown.year, month: shown.month } : null,
  );
  const flowState = showFamily ? familyFlow : personalFlow;
  // "Chi bất thường" is a per-person planning figure (§10) and its RPC takes a subject, so
  // the family view leaves it out rather than showing one person's history under a family
  // heading.
  const unexpected = useUnexpected(subjectId === '' || showFamily ? null : subjectId);

  if (contextState.status === 'loading') return <LoadingState label="Đang tải báo cáo…" />;
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Báo cáo</h1>

      {isFamily ? <ScopeSwitch scope={scope} onChange={setScope} /> : null}

      <MonthSwitcher period={shown} current={current} onChange={setPeriod} />

      {flowState.status === 'loading' ? <LoadingState label="Đang tính…" /> : null}
      {flowState.status === 'error' ? (
        <ErrorState message={flowState.message} onRetry={flowState.retry} />
      ) : null}

      {flowState.status === 'ready' ? (
        <>
          <MoneyInCard moneyIn={flowState.flow.moneyIn} />
          {/* The family view has no dashboard of its own — Trang chủ is one person's — so it
              carries the allocation and the category breakdown here. */}
          {showFamily ? (
            <>
              <AllocationCard
                moneyIn={flowState.flow.moneyIn}
                allocation={flowState.flow.allocation}
              />
              <FamilyExpenseCard expense={flowState.flow.expense} />
            </>
          ) : null}
          <NatureCard
            headingId={natureHeadingId}
            slices={flowState.flow.expense.byPlanningNature}
            total={flowState.flow.expense.total}
          />
        </>
      ) : null}

      {unexpected.status === 'loading' ? <LoadingState label="Đang đọc lịch sử…" /> : null}
      {unexpected.status === 'error' ? (
        <ErrorState message={unexpected.message} onRetry={unexpected.retry} />
      ) : null}
      {unexpected.status === 'ready' ? <UnexpectedCard summary={unexpected.summary} /> : null}
    </div>
  );
}

type ReportScope = 'me' | 'family';

/**
 * `Me | Gia đình` (spec 22.3). Real tabs rather than two buttons that look like tabs: the
 * cards below are the panel they control, and a screen reader has to be told that.
 */
function ScopeSwitch({
  scope,
  onChange,
}: {
  scope: ReportScope;
  onChange: (scope: ReportScope) => void;
}) {
  const options: ReadonlyArray<{ id: ReportScope; label: string }> = [
    { id: 'me', label: 'Tôi' },
    { id: 'family', label: 'Gia đình' },
  ];

  return (
    <div className={styles.scope} role="tablist" aria-label="Báo cáo của ai">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          aria-selected={scope === option.id}
          className={cx(styles.scopeTab, scope === option.id && styles.scopeTabActive)}
          onClick={() => onChange(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * What the household spent, by category group.
 *
 * Rolled up to the parent category on purpose: at family level "Ăn uống 6.2tr" is the answer,
 * and the child-level detail belongs to whoever posted it. Deliberately not the dashboard's
 * BreakdownCard — its rows link into a timeline that is scoped to one person, which would be
 * a lie under a family heading.
 */
function FamilyExpenseCard({ expense }: { expense: ExpenseBreakdown }) {
  if (expense.byParent.length === 0) {
    return (
      <section className={lists.card} aria-labelledby="family-expense-heading">
        <h2 className={lists.cardTitle} id="family-expense-heading">
          Gia đình chi vào đâu
        </h2>
        <p className={forms.hint}>Tháng này cả nhà chưa ghi khoản chi nào.</p>
      </section>
    );
  }

  return (
    <section className={lists.card} aria-labelledby="family-expense-heading">
      <h2 className={lists.cardTitle} id="family-expense-heading">
        Gia đình chi vào đâu
      </h2>
      <ul className={lists.list}>
        {expense.byParent.map((slice) => (
          <li key={slice.categoryId ?? 'uncategorised'}>
            <div className={styles.row}>
              <span className={lists.rowText}>
                <span className={lists.rowName}>{slice.name ?? 'Chưa phân loại'}</span>
                <span className={lists.rowMeta}>{slice.sharePct}%</span>
              </span>
              <span className={lists.amount}>{formatVnd(slice.amount)}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Money In, split the way B10 needs it: what arrives every month, and what merely arrived.
 *
 * The dashboard shows one total; the split matters here because only the dependable half is
 * what the household plans — and later invests — against.
 */
function MoneyInCard({ moneyIn }: { moneyIn: MoneyIn }) {
  const stable = moneyIn.bySource.filter((source) => isStable(source.stabilityClass));
  const variable = moneyIn.bySource.filter((source) => !isStable(source.stabilityClass));

  return (
    <section className={lists.card} aria-labelledby="money-in-heading">
      <h2 className={lists.cardTitle} id="money-in-heading">
        Tiền vào
      </h2>

      {moneyIn.bySource.length === 0 && moneyIn.receivedFromFamily === 0 ? (
        <p className={forms.hint}>Tháng này chưa ghi khoản thu nào.</p>
      ) : (
        <>
          <SourceGroup
            title="Ổn định — dựa vào được"
            total={moneyIn.stableTotal}
            sources={stable}
          />
          <SourceGroup
            title="Không đều — đừng lập kế hoạch quanh nó"
            total={moneyIn.variableTotal}
            sources={variable}
          />

          {moneyIn.bySource.length > 0 ? (
            <p className={styles.total}>
              <span>Tổng tiền vào</span>
              <strong>{formatVnd(moneyIn.total)}</strong>
            </p>
          ) : null}

          {/* Kept out of the total above and given its own line (spec 22.2): money from a
              spouse is not something this person earned, so it must not inflate the income
              they plan around — but it is money they now have. */}
          {moneyIn.receivedFromFamily !== 0 ? (
            <p className={styles.total}>
              <span>Nhận từ gia đình</span>
              <strong>{formatVnd(moneyIn.receivedFromFamily)}</strong>
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function SourceGroup({
  title,
  total,
  sources,
}: {
  title: string;
  total: number;
  sources: MoneyIn['bySource'];
}) {
  if (sources.length === 0) return null;

  return (
    <>
      <p className={styles.groupHeading}>
        <span>{title}</span>
        <strong>{formatVnd(total)}</strong>
      </p>
      <ul className={lists.list}>
        {sources.map((source) => (
          <li key={source.incomeSourceId}>
            <div className={styles.row}>
              <span className={lists.rowText}>
                <span className={lists.rowName}>{source.name}</span>
                <span className={lists.rowMeta}>
                  {STABILITY_LABELS[source.stabilityClass]} · {source.sharePct}%
                </span>
              </span>
              <span className={lists.amount}>{formatVnd(source.amount)}</span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Spending by nature (§10).
 *
 * A line the person never labelled still appears here, under the default its category's group
 * implies (N11 hướng A) — otherwise this card would be mostly empty and say nothing. The two
 * natures that are never defaulted, "Ngoài dự kiến" and "Khẩn cấp", are marked so it is clear
 * which figures are somebody's judgement rather than the app's.
 */
function NatureCard({
  headingId,
  slices,
  total,
}: {
  headingId: string;
  slices: readonly PlanningNatureSlice[];
  total: number;
}) {
  return (
    <section className={lists.card} aria-labelledby={headingId}>
      <h2 className={lists.cardTitle} id={headingId}>
        Chi theo tính chất
      </h2>

      {slices.length === 0 ? (
        <p className={forms.hint}>Tháng này chưa có khoản chi nào.</p>
      ) : (
        <>
          <ul className={lists.list}>
            {slices.map((slice) => {
              const declaredOnly =
                slice.planningNature === 'unexpected' || slice.planningNature === 'emergency';

              return (
                <li key={slice.planningNature ?? 'unknown'}>
                  <div className={styles.row}>
                    <span className={lists.rowText}>
                      <span className={lists.rowName}>
                        {slice.planningNature === null
                          ? 'Chưa xếp nhóm'
                          : PLANNING_NATURE_LABELS[slice.planningNature]}
                      </span>
                      <span className={lists.rowMeta}>
                        {slice.sharePct}%{declaredOnly ? ' · bạn tự đánh dấu' : ''}
                      </span>
                    </span>
                    <span className={cx(lists.amount, declaredOnly && styles.declared)}>
                      {formatVnd(slice.amount)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>

          <p className={styles.total}>
            <span>Tổng chi</span>
            <strong>{formatVnd(total)}</strong>
          </p>

          <p className={forms.hint}>
            Khoản nào bạn không chọn “Tính chất” thì lấy theo nhóm của danh mục. Riêng “Ngoài dự
            kiến” và “Khẩn cấp” chỉ có khi bạn tự đánh dấu — nên số ở dưới đáng tin.
          </p>
        </>
      )}
    </section>
  );
}

/** The §10 answer: how much this household should be holding back each month. */
function UnexpectedCard({ summary }: { summary: UnexpectedSummary }) {
  if (summary.months.length === 0) {
    return (
      <section className={lists.card} aria-labelledby="unexpected-heading">
        <h2 className={lists.cardTitle} id="unexpected-heading">
          Chi bất thường
        </h2>
        <EmptyState
          icon="🌦️"
          title="Chưa có khoản nào được đánh dấu"
          message="Khi ghi một khoản ngoài dự kiến, chọn Tính chất là “Ngoài dự kiến” hoặc “Khẩn cấp”. Sau vài tháng, chỗ này sẽ nói bạn cần để dành bao nhiêu."
        />
      </section>
    );
  }

  return (
    <section className={lists.card} aria-labelledby="unexpected-heading">
      <h2 className={lists.cardTitle} id="unexpected-heading">
        Chi bất thường — {summary.monthsCovered} tháng gần nhất
      </h2>

      <p className={styles.headline}>{formatVnd(summary.suggestedMonthlyReserve)}/tháng</p>
      <p className={forms.hint}>
        Trung bình {formatVnd(summary.total)} trong {summary.monthsCovered} tháng. Tháng nặng nhất
        là {formatVnd(summary.worstMonthTotal)} — quỹ dự phòng cần chịu được mức đó, không chỉ theo
        kịp mức trung bình.
      </p>

      <ul className={lists.list}>
        {summary.months.map((month) => (
          <li key={month.periodStart}>
            <div className={styles.row}>
              <span className={lists.rowName}>{monthLabel(month.periodStart)}</span>
              <span className={lists.amount}>{formatVnd(month.total)}</span>
            </div>
            <ul className={styles.legs}>
              {month.rows.map((row, index) => (
                <li key={`${row.categoryId ?? 'none'}-${row.planningNature}-${index}`}>
                  <div className={styles.leg}>
                    <span>
                      {row.categoryName ?? 'Không có danh mục'}
                      {row.planningNature === 'emergency' ? ' · khẩn cấp' : ''}
                    </span>
                    <span className={styles.legAmount}>{formatVnd(row.amount)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}
